import React, { createContext, useContext, useState, useEffect } from 'react';
import { db, appId } from '../config/firebase';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, increment, onSnapshot, collection, addDoc, arrayUnion } from 'firebase/firestore';
import { useAuth } from './AuthContext';

import { AVATARS } from '../data/avatars';
import { ACHIEVEMENTS } from '../data/achievements';

const GamificationContext = createContext();

export const useGamification = () => {
    return useContext(GamificationContext);
};

export const GamificationProvider = ({ children }) => {
    const { user } = useAuth();
    const [levelData, setLevelData] = useState({
        currentLevel: 1,
        currentXP: 0,
        totalXP: 0,
        levelTitle: 'Novice',
        streakDays: 0,
        lastLoginDate: null,
        avatarId: 'novice_adventurer',
        avatarId: 'novice_adventurer',
        unlockedAvatars: ['novice_adventurer'],
        achievements: [],
        missions: null
    });
    const [loading, setLoading] = useState(true);
    const [xpNotification, setXpNotification] = useState(null); // For toast/confetti
    const [newAchievement, setNewAchievement] = useState(null); // For achievement toast

    // ... (existing helper functions: getXPForLevel, getLevelFromXP, getLevelProgress, getLevelTitle) ...
    const getXPForLevel = (level) => {
        if (level <= 1) return 0;
        return Math.floor(100 * Math.pow(level - 1, 1.5));
    };

    const getLevelFromXP = (xp) => {
        let level = 1;
        while (getXPForLevel(level + 1) <= xp) {
            level++;
        }
        return level;
    };

    const getLevelProgress = (xp, currentLevel) => {
        const currentLevelXP = getXPForLevel(currentLevel);
        const nextLevelXP = getXPForLevel(currentLevel + 1);
        const xpNeeded = nextLevelXP - currentLevelXP;
        const xpGained = xp - currentLevelXP;
        return Math.min(100, Math.max(0, (xpGained / xpNeeded) * 100));
    };

    const getLevelTitle = (level) => {
        if (level >= 50) return 'Legendary Scholar';
        if (level >= 40) return 'Grand Master';
        if (level >= 30) return 'Master';
        if (level >= 20) return 'Expert';
        if (level >= 10) return 'Advanced';
        if (level >= 5) return 'Apprentice';
        return 'Novice';
    };

    // Initialize & Listen to Gamification Data
    useEffect(() => {
        if (!user) {
            setLevelData({
                currentLevel: 1,
                currentXP: 0,
                totalXP: 0,
                levelTitle: 'Novice',
                streakDays: 0,
                lastLoginDate: null,
                avatarId: 'novice_adventurer',
                unlockedAvatars: ['novice_adventurer'],
                achievements: []
            });
            setLoading(false);
            return;
        }

        const gamificationRef = doc(db, `artifacts/${appId}/users/${user.uid}/gamification/stats`);

        const unsubscribe = onSnapshot(gamificationRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setLevelData({
                    currentLevel: data.currentLevel || 1,
                    currentXP: data.currentXP || 0,
                    totalXP: data.totalXP || 0,
                    levelTitle: getLevelTitle(data.currentLevel || 1),
                    streakDays: data.streakDays || 0,
                    lastLoginDate: data.lastLogin?.toDate() || null,
                    avatarId: data.avatarId || 'novice_adventurer',
                    unlockedAvatars: data.unlockedAvatars || ['novice_adventurer'],
                    achievements: data.achievements || []
                });
            } else {
                // Initialize if not exists
                // Initialize if not exists
                const initialData = {
                    currentLevel: 1,
                    currentXP: 50, // Initial XP for first login
                    totalXP: 50,
                    streakDays: 1,
                    lastLogin: serverTimestamp(),
                    avatarId: 'novice_adventurer',
                    unlockedAvatars: ['novice_adventurer'],
                    achievements: [
                        {
                            id: 'first_login',
                            unlockedAt: new Date().toISOString()
                        }
                    ]
                };
                setDoc(gamificationRef, initialData).catch(err => console.error("Error creating gamification doc:", err));
            }
            setLoading(false);
        });

        // Check Daily Login Logic
        checkDailyLogin(user.uid);

        // Check Daily Missions
        initOrResetMissions(user.uid).then(tasks => {
            setLevelData(prev => ({ ...prev, missions: tasks }));
        });

        return () => unsubscribe();
    }, [user]);

    const checkDailyLogin = async (userId) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const gamificationRef = doc(db, `artifacts/${appId}/users/${userId}/gamification/stats`);
        const docSnap = await getDoc(gamificationRef);

        if (docSnap.exists()) {
            const data = docSnap.data();

            // SELF-HEALING: Check if first_login achievement is missing (for existing users)
            const hasFirstLogin = data.achievements?.some(a => a.id === 'first_login');
            if (!hasFirstLogin) {
                checkAchievements('first_login', true);
            }

            const lastLogin = data.lastLogin?.toDate();
            let newStreak = data.streakDays || 0;

            if (lastLogin) {
                const lastLoginDate = new Date(lastLogin);
                lastLoginDate.setHours(0, 0, 0, 0);

                const diffTime = Math.abs(today - lastLoginDate);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays === 1) {
                    newStreak += 1;
                    await updateDoc(gamificationRef, {
                        streakDays: increment(1),
                        lastLogin: serverTimestamp()
                    });
                    addXP(50, 'Login Harian (Streak)');
                } else if (diffDays > 1) {
                    newStreak = 1;
                    await updateDoc(gamificationRef, {
                        streakDays: 1,
                        lastLogin: serverTimestamp()
                    });
                    addXP(50, 'Login Harian');
                }
            } else {
                newStreak = 1;
                await updateDoc(gamificationRef, {
                    streakDays: 1,
                    lastLogin: serverTimestamp()
                });
                // Fallback for missing lastLogin, though unexpected with new init
                if (!hasFirstLogin) {
                    // Achievement handles XP, but if we want explicit log:
                    // addXP(50, 'Login Pertama'); // Already handled by checkAchievements if missing
                }
            }

            // Check Streak Achievements
            if (newStreak >= 3) checkAchievements('streak_3', true);
            if (newStreak >= 7) checkAchievements('streak_7', true);
        }
    };

    const addXP = async (amount, reason) => {
        if (!user) return;

        try {
            const gamificationRef = doc(db, `artifacts/${appId}/users/${user.uid}/gamification/stats`);
            const historyRef = collection(db, `artifacts/${appId}/users/${user.uid}/gamification_history`);

            await addDoc(historyRef, {
                amount: amount,
                reason: reason,
                timestamp: serverTimestamp()
            });

            const docSnap = await getDoc(gamificationRef);
            let currentStats = docSnap.exists() ? docSnap.data() : { totalXP: 0, currentLevel: 1 };

            let newTotalXP = (currentStats.totalXP || 0) + amount;
            let newLevel = getLevelFromXP(newTotalXP);
            let levelUp = newLevel > (currentStats.currentLevel || 1);

            await updateDoc(gamificationRef, {
                totalXP: newTotalXP,
                currentXP: newTotalXP,
                currentLevel: newLevel
            });

            // Check Level Up Achievements
            if (levelUp) {
                if (newLevel >= 5) checkAchievements('level_5', true);
                if (newLevel >= 10) checkAchievements('level_10', true);
            }

            setXpNotification({
                amount,
                reason,
                levelUp,
                newLevel: levelUp ? newLevel : null
            });

            setTimeout(() => setXpNotification(null), 3000);

            console.log(`🎉 XP Gained: ${amount} (${reason}). New Total: ${newTotalXP}, Level: ${newLevel}`);

        } catch (error) {
            console.error("Error adding XP:", error);
        }
    };

    const equipAvatar = async (avatarId) => {
        if (!user) return;
        const gamificationRef = doc(db, `artifacts/${appId}/users/${user.uid}/gamification/stats`);
        await updateDoc(gamificationRef, { avatarId });
    };

    const unlockAvatar = async (avatarId) => {
        if (!user) return;
        const gamificationRef = doc(db, `artifacts/${appId}/users/${user.uid}/gamification/stats`);
        await updateDoc(gamificationRef, {
            unlockedAvatars: arrayUnion(avatarId)
        });
    };

    const checkAchievements = async (achievementId, conditionMet) => {
        if (!user || !conditionMet) return;

        const gamificationRef = doc(db, `artifacts/${appId}/users/${user.uid}/gamification/stats`);
        const docSnap = await getDoc(gamificationRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            const existingAchievements = data.achievements || [];

            // Check if already unlocked
            if (existingAchievements.some(a => a.id === achievementId)) return;

            // Unlock logic
            const achievement = ACHIEVEMENTS.find(a => a.id === achievementId);
            if (!achievement) return;

            const newAchievementEntry = {
                id: achievementId,
                unlockedAt: new Date().toISOString()
            };

            await updateDoc(gamificationRef, {
                achievements: [...existingAchievements, newAchievementEntry]
            });

            // Optional: Give XP for achievement?
            if (achievement.xpReward) {
                addXP(achievement.xpReward, `Achievement: ${achievement.title}`);
            }

            // Show Toast
            setNewAchievement(achievement);
            setTimeout(() => setNewAchievement(null), 4000);
        }
    };

    // --- DAILY MISSIONS LOGIC ---

    const initOrResetMissions = async (userId, forceReset = false) => {
        const missionsRef = doc(db, `artifacts/${appId}/users/${userId}/gamification/missions`);
        const docSnap = await getDoc(missionsRef);
        const todayStr = new Date().toDateString();

        let shouldReset = forceReset;
        if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.lastReset !== todayStr) {
                shouldReset = true;
            }
        } else {
            shouldReset = true;
        }

        if (shouldReset) {
            const newMissions = {
                lastReset: todayStr,
                tasks: {
                    login: { id: 'login', title: 'Login Harian', target: 1, current: 1, completed: true, claimed: false, xp: 50 },
                    quiz_1: { id: 'quiz_1', title: 'Selesaikan 1 Quiz', target: 1, current: 0, completed: false, claimed: false, xp: 100 },
                    materi_1: { id: 'materi_1', title: 'Baca 1 Materi', target: 1, current: 0, completed: false, claimed: false, xp: 75 },
                }
            };
            await setDoc(missionsRef, newMissions);
            return newMissions.tasks;
        }

        return docSnap.data().tasks;
    };

    const checkMission = async (actionType) => {
        if (!user) return;
        const missionsRef = doc(db, `artifacts/${appId}/users/${user.uid}/gamification/missions`);
        const docSnap = await getDoc(missionsRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            const todayStr = new Date().toDateString();

            if (data.lastReset !== todayStr) {
                // Should have been reset by initOrResetMissions called elsewhere, but just in case
                await initOrResetMissions(user.uid);
                return;
            }

            let tasks = { ...data.tasks };
            let updated = false;

            // Map actions to mission IDs
            let missionId = null;
            if (actionType === 'quiz_complete') missionId = 'quiz_1';
            if (actionType === 'materi_complete') missionId = 'materi_1';

            if (missionId && tasks[missionId]) {
                const task = tasks[missionId];
                if (!task.completed) {
                    task.current += 1;
                    if (task.current >= task.target) {
                        task.completed = true;
                        // Notify user? maybe via toast later
                    }
                    updated = true;
                }
            }

            if (updated) {
                await updateDoc(missionsRef, { tasks });
                // Update local state if we had one expose missions in context
                setLevelData(prev => ({ ...prev, missions: tasks }));
            }
        }
    };

    const claimMissionReward = async (missionId) => {
        if (!user) return;
        const missionsRef = doc(db, `artifacts/${appId}/users/${user.uid}/gamification/missions`);
        const docSnap = await getDoc(missionsRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            let tasks = { ...data.tasks };
            const task = tasks[missionId];

            if (task && task.completed && !task.claimed) {
                task.claimed = true;
                await updateDoc(missionsRef, { tasks });

                // Award XP
                addXP(task.xp, `Misi Harian: ${task.title}`);

                // Update local state
                setLevelData(prev => ({ ...prev, missions: tasks }));
            }
        }
    };

    const value = {
        ...levelData,
        loading,
        addXP,
        equipAvatar,
        checkAchievements, // Exporting for manual checks if needed
        checkMission,
        claimMissionReward,
        xpNotification,
        newAchievement,
        getLevelProgress,
        getXPForLevel
    };

    return (
        <GamificationContext.Provider value={value}>
            {children}
        </GamificationContext.Provider>
    );
};
